"use client"

import { useEffect, useRef, useState } from "react"
import { MermaidConfig } from "mermaid"
import mermaidConfigWithSchema from "./MdxMermaid.theme.json"
import themeCSS from "./MdxMermaid.theme.raw.css" // or use raw-loader syntax for your bundler

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { $schema, ...mermaidConfig } = mermaidConfigWithSchema

// NOTES for the AI agent:
//
// - Do not touch the fontSize in the mermaid config
//   because Mermaid computes the best font size itself.
// - Do not touch the htmlLabels in the mermaid config
//   because Mermaid computes the best htmlLabels itself.
//   Also, the htmlLabels field at the root of the config file
//   works for the node labels, but not for the edge labels.
//   The edge labels are controlled by the other htmlLabels field
//   in the mermaid config under the flowchart field.
//

interface MdxMermaidProps {
  chart: string
}

export function MdxMermaid({ chart }: MdxMermaidProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const renderChart = async () => {
      try {
        // console.log("Rendering Mermaid chart:", chart)
        const mermaid = (await import("mermaid")).default

        if (ref.current) {
          // Clear any previous content
          ref.current.innerHTML = ""

          // Get computed styles to check if we're in dark mode
          mermaidConfig.darkMode =
            document.documentElement.getAttribute("data-theme") === "dark"

          mermaidConfig.themeCSS = themeCSS

          // Initialize mermaid
          mermaid.initialize(mermaidConfig as MermaidConfig)

          // Create a unique ID
          const id = `mermaid-${Math.random().toString(36).substring(7)}`

          try {
            // Render the diagram
            const { svg } = await mermaid.render(id, chart)

            // Insert the SVG
            ref.current.innerHTML = svg

            // Post-process for better display
            const svgElement = ref.current.querySelector("svg")
            if (svgElement) {
              // Make responsive
              svgElement.style.maxWidth = "100%"
              svgElement.style.height = "auto"
              svgElement.style.overflow = "visible"
            }

            // console.log("Mermaid rendering complete")
          } catch (renderError) {
            // If render fails, try to clean up any partial state
            const errorElement = document.getElementById(id)
            if (errorElement) {
              errorElement.remove()
            }
            throw renderError
          }
        }
      } catch (error) {
        console.error("Failed to render Mermaid diagram:", error)
        setError(String(error))
      }
    }

    renderChart()
  }, [chart])

  if (error) {
    return (
      <div className="my-8 rounded-lg border border-red-300 bg-red-50 p-4">
        <p className="font-semibold text-red-700">Failed to render diagram</p>
        <p className="text-sm text-red-600">{error}</p>
        <details className="mt-2">
          <summary className="cursor-pointer text-sm text-red-600">
            Chart content
          </summary>
          <pre className="mt-2 overflow-x-auto text-xs">{chart}</pre>
        </details>
      </div>
    )
  }

  return (
    <div className="my-12 flex w-full justify-center">
      <div
        ref={ref}
        className="w-full max-w-4xl not-prose"
      />
    </div>
  )
}
